import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate, fmtTRY } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Wrench, Search, Phone } from "lucide-react";

const WO_STATUS = {
  kabul: { label: "Kabul Edildi", color: "bg-slate-500 text-white" },
  parca_bekliyor: { label: "Parça Bekliyor", color: "bg-amber-500 text-slate-900" },
  onarimda: { label: "Onarımda", color: "bg-[#EA580C] text-white" },
  test: { label: "Test Edildi", color: "bg-blue-500 text-white" },
  teslim: { label: "Teslim Edildi", color: "bg-emerald-600 text-white" },
};

export default function CustomerServiceRequestsPage() {
  const [wos, setWos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/work-orders").then((r) => setWos(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    wos.forEach((w) => {
      const key = w.customer_id || `noid:${w.customer_name}`;
      if (!map.has(key)) {
        const c = customers.find((x) => x.id === w.customer_id);
        map.set(key, {
          key,
          customer_id: w.customer_id,
          name: w.customer_name,
          phone: c?.phone || "",
          items: [],
        });
      }
      map.get(key).items.push(w);
    });
    // sort items by created desc, compute counts and latest
    const arr = Array.from(map.values()).map((g) => {
      g.items.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      g.latest = g.items[0];
      g.total_open = g.items.filter((x) => x.status !== "teslim").length;
      g.total_done = g.items.filter((x) => x.status === "teslim").length;
      return g;
    });
    arr.sort((a, b) => b.total_open - a.total_open || (b.latest?.created_at || "").localeCompare(a.latest?.created_at || ""));
    return arr;
  }, [wos, customers]);

  const filtered = grouped.filter((g) => (g.name + " " + (g.phone || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="customer-service-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Müşteri Servis Takibi</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Servis Talepleri</h1>
        </div>
        <Button className="bg-[#D97706] hover:bg-[#B45309] text-white gap-2" onClick={() => nav("/servis")}>Yeni İş Emri</Button>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Müşteri adı/telefon ile ara…" className="pl-9" data-testid="csr-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {filtered.map((g) => (
          <Card key={g.key} className="border-slate-200 tp-fade" data-testid={`csr-card-${g.key}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 text-lg truncate">{g.name}</div>
                  {g.phone && <div className="text-xs text-slate-500 flex items-center gap-1"><Phone size={12} />{g.phone}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {g.total_open > 0 && <Badge className="bg-[#D97706] text-white">{g.total_open} Açık</Badge>}
                  {g.total_done > 0 && <Badge variant="outline">{g.total_done} Tamamlandı</Badge>}
                </div>
              </div>
              <div className="space-y-2">
                {g.items.slice(0, 4).map((w) => (
                  <div key={w.id} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-slate-500">{w.wo_no}</span>
                          <Badge className={`text-[10px] ${WO_STATUS[w.status]?.color}`}>{WO_STATUS[w.status]?.label}</Badge>
                        </div>
                        <div className="text-sm font-semibold text-slate-800 truncate">
                          <Wrench size={12} className="inline mr-1 text-slate-500" />
                          {w.tractor_brand} {w.tractor_model} {w.plate && `· ${w.plate}`}
                        </div>
                        {w.complaint && <div className="text-xs text-slate-500 line-clamp-1">{w.complaint}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] text-slate-400">{fmtDate(w.created_at)}</div>
                        <div className="text-sm font-bold text-[#1B4D3E]">{fmtTRY(w.total)}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {g.items.length > 4 && <div className="text-xs text-slate-400 text-center">+{g.items.length - 4} tane daha</div>}
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Müşteri servis kaydı yok.</div>}
      </div>
    </div>
  );
}
